import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { FormControl } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { debounceTime, Subject } from 'rxjs';
import {
  CACHE_NAMES,
  CHANNEL_TYPE,
  CHANNEL_TYPES_OBJECT
} from '../../../../../enum/constants';
import { Channel } from '../../../../../modules/crm/channel/components/channel/channel.interface';
import { BaseService } from '../../../../../services/base.service';
import { CacheService } from '../../../../../services/cache.service';
import { ChannelService } from '../../../../../services/channel.service';
import { ChatService } from '../../../../../services/chat.service';
import { ModalService } from '../../../../../services/modal.service';
import { ChatTag, TagService } from '../../../../../services/tag.service';
import { LEFT_MENU, MenuItem } from '../../chat-sidebar.interface';
import {
  CHAT_CONVERSATION_TYPE,
  CHAT_FILTER_GROUP,
  CHAT_FILTER_NONE,
  CHAT_SORT,
  CHAT_TABS,
  IChatFilterOption,
  IChatListMenuItem,
  MENU_ITEM_TYPE,
} from './chat-list.interface';

// Menu items that live in the separate filter menu on desktop
const FILTER_MENU_ITEM_KEYS = ['channel', 'tag', 'manager', 'sort', 'conversation_type', 'conversation_exclusive', 'tabs', 'clear_filters'];

@Component({
  selector: 'app-chat-list',
  templateUrl: './chat-list.component.html',
  styleUrl: './chat-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatListComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() selectedTabKey!: string;
  private readonly destroy$: Subject<null> = new Subject<null>();
  private searchTerms = new Subject<string>();
  private leftMenu!: HTMLElement | null;
  protected readonly chatListMenuItems: IChatListMenuItem[] = [
    {
      key: 'search',
      title: 'search',
      icon: 'search',
      type: MENU_ITEM_TYPE.BUTTON,
      action: () => this.toggleSearch(),
    },
    {
      key: 'write_message',
      title: 'write_message',
      icon: 'chat_add_on',
      type: MENU_ITEM_TYPE.BUTTON,
      action: () => this.writeMessage(),
    },
    {
      key: 'reset',
      title: 'update_chats',
      icon: 'refresh',
      type: MENU_ITEM_TYPE.BUTTON,
      action: () => this.chatService.resetAndInitChatList(),
    },
    {
      key: 'channel',
      title: 'channel',
      icon: 'hub',
      type: MENU_ITEM_TYPE.CHANNELS_DROPDOWN,
    },
    {
      key: 'tag',
      title: 'chat_tags',
      icon: 'sell',
      type: MENU_ITEM_TYPE.TAGS_DROPDOWN,
    },
    {
      key: 'manager',
      title: 'manager',
      icon: 'person',
      type: MENU_ITEM_TYPE.MANAGERS_DROPDOWN,
    },
    {
      key: 'sort',
      title: 'sort.sort',
      icon: 'sort',
      type: MENU_ITEM_TYPE.TOGGLE,
      toggleOptions: [
        { label: 'sort.new', value: CHAT_SORT.IS_NEW },
        { label: 'sort.unread', value: CHAT_SORT.IS_UNREAD },
      ],
      model: this.chatService.selectedChatSort,
      onChange: (e: MatButtonToggleChange) => this.setSort(e.value),
    },
    {
      key: 'conversation_type',
      title: 'conversation_type.title',
      icon: 'comment',
      type: MENU_ITEM_TYPE.TOGGLE,
      toggleOptions: [
        { label: 'conversation_type.all', value: CHAT_CONVERSATION_TYPE.ALL },
        { label: 'conversation_type.chats', value: CHAT_CONVERSATION_TYPE.CHATS },
        { label: 'conversation_type.comments', value: CHAT_CONVERSATION_TYPE.COMMENTS },
      ],
      model: this.chatService.selectedConversationType,
      onChange: (e: MatButtonToggleChange) => this.setConversationType(e.value),
    },
    {
      key: 'conversation_exclusive',
      title: 'conversation_type.exclude',
      icon: 'comment',
      type: MENU_ITEM_TYPE.TOGGLE,
      toggleOptions: [
        { label: 'no', value: false },
        { label: 'yes', value: true },
      ],
      model: this.chatService.isConversationTypeExclusive,
      onChange: (e: MatButtonToggleChange) => this.setConversationExclusive(e.value),
    },
    {
      key: 'tabs',
      title: 'tabs.chats',
      icon: 'forum',
      type: MENU_ITEM_TYPE.TOGGLE,
      toggleOptions: [
        { label: 'tabs.opened', value: CHAT_TABS.ACTIVE },
        { label: 'tabs.closed', value: CHAT_TABS.CLOSED },
        { label: 'tabs.spam', value: CHAT_TABS.SPAM },
      ],
      model: this.chatService.selectedChatTab,
      onChange: (e: MatButtonToggleChange) => this.setTab(e.value),
    },
    {
      key: 'clear_filters',
      title: 'clear_filters',
      icon: 'filter_alt_off',
      type: MENU_ITEM_TYPE.BUTTON,
      action: () => this.clearFilters(),
    },
    {
      key: 'settings',
      title: 'settings',
      icon: 'settings',
      type: MENU_ITEM_TYPE.BUTTON,
      action: () => this.openSettings(),
    },
    {
      key: 'clear_media_cache',
      title: 'clear_media_cache',
      icon: 'cached',
      type: MENU_ITEM_TYPE.BUTTON,
      action: () => this.clearMediaCache(),
    },
  ];
  protected readonly filterMenuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];
  protected readonly sortOptions = [
    { label: 'sort.new', value: CHAT_SORT.IS_NEW },
    { label: 'sort.unread', value: CHAT_SORT.IS_UNREAD },
  ];
  protected readonly conversationTypeOptions = [
    { label: 'conversation_type.all', value: CHAT_CONVERSATION_TYPE.ALL },
    { label: 'conversation_type.chats', value: CHAT_CONVERSATION_TYPE.CHATS },
    { label: 'conversation_type.comments', value: CHAT_CONVERSATION_TYPE.COMMENTS },
  ];
  protected readonly tabOptions = [
    { label: 'tabs.opened', value: CHAT_TABS.ACTIVE },
    { label: 'tabs.closed', value: CHAT_TABS.CLOSED },
    { label: 'tabs.spam', value: CHAT_TABS.SPAM },
  ];
  protected readonly filterGroups: { key: CHAT_FILTER_GROUP; title: string; icon: string }[] = [
    { key: 'channel', title: 'channels', icon: 'hub' },
    { key: 'tag', title: 'chat_tags', icon: 'sell' },
    { key: 'manager', title: 'managers', icon: 'person' },
  ];
  protected readonly FILTER_SEARCH_THRESHOLD = 7;
  protected readonly CHAT_FILTER_NONE = CHAT_FILTER_NONE;
  protected filterQuery: Record<CHAT_FILTER_GROUP, string> = { channel: '', tag: '', manager: '' };
  protected channels: Channel[] = [];
  protected tags: ChatTag[] = [];
  protected managers: Array<{ id: string; name: string; img_url?: string }> = [];
  protected formChannels!: FormControl;
  protected formTags!: FormControl;
  protected formManagers!: FormControl;
  protected readonly menuItems: MenuItem[] = this.chatService.menuItems;
  protected isSearch: boolean = false;
  protected hasFilters: boolean = false;
  protected isShowWriteMessage: boolean = false;

  constructor(
    private readonly renderer: Renderer2,
    private readonly cacheService: CacheService,
    private readonly channelService: ChannelService,
    protected readonly baseService: BaseService,
    protected readonly chatService: ChatService,
    protected readonly modalService: ModalService,
    private readonly tagService: TagService,
  ) {}

  ngOnInit() {
    this.initSearch();
    this.initMenu();
    this.chatService.resetAndInitChatList();
  }

  ngAfterViewInit() {
    this.leftMenu = document.getElementById('leftMenu');
    this.initChatListWidth();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['selectedTabKey'].isFirstChange()) {
      this.chatService.resetAndInitChatList();
      // The conversation type filter is available only on some tabs
      this.initFiltersBadge();
    }
  }

  ngOnDestroy() {
    this.chatService.chatsInfo.search_text = '';
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initChatListWidth(): void {
    const chatListWidth = localStorage.getItem('leftMenuWidth');
    if (chatListWidth && this.leftMenu) {
      this.leftMenu.style.setProperty('--left-menu-width', chatListWidth);
    }
  }

  private initSearch(): void {
    this.searchTerms.pipe(debounceTime(300)).subscribe((text: string) => {
      this.chatService.resetChatsInfo();
      this.chatService.chatsInfo.search_text = text;
      this.chatService.initChats();
    });
  }

  private initMenu(): void {
    this.checkIsShowWriteMessage();

    const selectedChatTab = localStorage.getItem('selectedChatTab');
    if (selectedChatTab) {
      this.chatService.selectedChatTab = selectedChatTab as CHAT_TABS;
      const tabsMenuItem = this.chatListMenuItems.find((v) => v.key === 'tabs');
      if (tabsMenuItem) {
        tabsMenuItem.model = this.chatService.selectedChatTab;
      }
    }

    const selectedChatSort = localStorage.getItem('selectedChatSort');
    if (selectedChatSort) {
      this.chatService.selectedChatSort = selectedChatSort as CHAT_SORT;
      const sortMenuItem = this.chatListMenuItems.find((v) => v.key === 'sort');
      if (sortMenuItem) {
        sortMenuItem.model = this.chatService.selectedChatSort;
      }
    }

    const selectedConversationType = localStorage.getItem('selectedChatConversationType');
    if (selectedConversationType) {
      this.chatService.selectedConversationType = selectedConversationType as CHAT_CONVERSATION_TYPE;
      const conversationTypeMenuItem = this.chatListMenuItems.find((v) => v.key === 'conversation_type');
      if (conversationTypeMenuItem) {
        conversationTypeMenuItem.model = this.chatService.selectedConversationType;
      }
    }

    this.chatService.isConversationTypeExclusive = localStorage.getItem('selectedChatConversationExclusive') === 'true';
    this.syncConversationExclusiveMenuItem();

    this.initChannels();
    this.initTags();
    this.initManagers();

    this.initFiltersBadge();
  }

  private initChannels() {
    const selectedChannels = localStorage.getItem('selectedChannels');
    if (selectedChannels) {
      this.chatService.selectedChannels = JSON.parse(selectedChannels);
    }

    this.formChannels = new FormControl(this.chatService.selectedChannels);

    this.channelService.getChannels({ scope: 'chats' }).subscribe((res) => {
      if (!res) return;

      res.forEach((item: any) => {
        item['img_url'] = CHANNEL_TYPES_OBJECT[item.type as CHANNEL_TYPE]?.img_url;
      });
      this.channels = res;
    });
  }

  private initTags(): void {
    const selectedTags = localStorage.getItem('selectedChatTags');
    if (selectedTags) {
      this.chatService.selectedTags = JSON.parse(selectedTags);
    }
    this.formTags = new FormControl(this.chatService.selectedTags);
    this.tagService.getTags().subscribe({
      next: (tags) => (this.tags = tags),
      error: () => (this.tags = []),
    });
  }

  private initManagers(): void {
    const selectedManagers = localStorage.getItem('selectedChatManagers');
    if (selectedManagers) {
      this.chatService.selectedManagers = JSON.parse(selectedManagers);
    }
    this.formManagers = new FormControl(this.chatService.selectedManagers);
    this.chatService.getManagers().subscribe((managers) => {
      this.managers = managers.map((manager) => ({
        id: manager.id,
        name: manager.name,
        img_url: manager.avatar_url ?? undefined,
      }));
    });
  }

  private initFiltersBadge() {
    const isSortUnread = this.chatService.selectedChatSort !== CHAT_SORT.IS_NEW;
    // On desktop chat tabs live under the search, so they don't count as a filter
    const isTabClosed = this.baseService.isMobile && this.chatService.selectedChatTab !== CHAT_TABS.ACTIVE;
    const hasConversationTypeFilter = this.chatService.appliedConversationType !== CHAT_CONVERSATION_TYPE.ALL;
    const hasChannelFilters = !!this.chatService.selectedChannels.length;
    const hasTagFilters = !!this.chatService.selectedTags.length;
    const hasManagerFilters = !!this.chatService.selectedManagers.length;

    this.hasFilters =
      isSortUnread || isTabClosed || hasConversationTypeFilter || hasChannelFilters || hasTagFilters || hasManagerFilters;
  }

  protected isMenuButtonVisible(item: IChatListMenuItem): boolean {
    let condition = true;
    if (!this.baseService.isMobile && FILTER_MENU_ITEM_KEYS.includes(item.key)) return false;
    if (item.key === 'conversation_type') condition = this.chatService.isConversationTypeAvailable();
    if (item.key === 'conversation_exclusive') condition = this.chatService.appliedConversationType !== CHAT_CONVERSATION_TYPE.ALL;
    if (item.title === 'search') condition = this.baseService.isMobile;
    if (item.title === 'clear_media_cache') condition = this.baseService.currentCompanyInfo.settings?.is_telegram_user;
    if (item.title === 'write_message') condition = this.baseService.isMobile && this.isShowWriteMessage;
    return condition;
  }

  private clearMediaCache() {
    this.cacheService.clearCache(CACHE_NAMES.CHAT_MEDIA);
    this.cacheService.clearCache(CACHE_NAMES.CHAT_PROFILE_PHOTO);
  }

  protected onSearch(text: string) {
    this.searchTerms.next(text);
  }

  protected scrollTo(event: any) {
    const chatsInfo = this.chatService.chatsInfo;
    if (
      this.chatService.isLoaderChatList.center ||
      this.chatService.isLoaderChatList.down ||
      this.chatService.isLoaderChatList.up
    )
      return;
    if (event.target.scrollHeight - event.target.scrollTop <= event.target.clientHeight + 700) {
      const candidatePage = (chatsInfo.loaded_pages[chatsInfo.loaded_pages.length - 1] || 0) + 1;
      if (candidatePage && chatsInfo.page_number < chatsInfo.page_count) {
        chatsInfo.page_number = candidatePage;
        this.chatService.initChats(true, { direction: 'down' }, true);
      }
    }
    if (event.target.scrollTop <= 350) {
      if (chatsInfo.loaded_pages.length) {
        const candidatePage = chatsInfo.loaded_pages[0] - 1;
        if (candidatePage >= 1) {
          chatsInfo.page_number = candidatePage;
          this.chatService.initChats(true, { direction: 'up', event_scroll: event }, true);
        }
      }
    }
  }

  protected setTab(value: CHAT_TABS) {
    if (this.chatService.selectedChatTab === value) return;
    this.chatService.selectedChatTab = value;
    const tabsMenuItem = this.chatListMenuItems.find((item) => item.key === 'tabs');
    if (tabsMenuItem) tabsMenuItem.model = value;
    this.chatService.resetAndInitChatList();
    this.initFiltersBadge();
    localStorage.setItem('selectedChatTab', this.chatService.selectedChatTab);
  }

  protected setSort(value: CHAT_SORT) {
    if (this.chatService.selectedChatSort === value) return;
    this.chatService.selectedChatSort = value;
    const sortMenuItem = this.chatListMenuItems.find((item) => item.key === 'sort');
    if (sortMenuItem) sortMenuItem.model = value;
    this.chatService.resetAndInitChatList();
    this.initFiltersBadge();
    localStorage.setItem('selectedChatSort', this.chatService.selectedChatSort);
  }

  protected setConversationType(value: CHAT_CONVERSATION_TYPE) {
    if (this.chatService.selectedConversationType === value) return;
    this.chatService.selectedConversationType = value;
    const conversationTypeMenuItem = this.chatListMenuItems.find((item) => item.key === 'conversation_type');
    if (conversationTypeMenuItem) conversationTypeMenuItem.model = value;
    this.syncConversationExclusiveMenuItem();
    this.chatService.resetAndInitChatList();
    this.initFiltersBadge();
    localStorage.setItem('selectedChatConversationType', this.chatService.selectedConversationType);
  }

  protected setConversationExclusive(value: boolean) {
    if (this.chatService.isConversationTypeExclusive === value) return;
    this.chatService.isConversationTypeExclusive = value;
    this.syncConversationExclusiveMenuItem();
    this.chatService.resetAndInitChatList();
    this.initFiltersBadge();
    localStorage.setItem('selectedChatConversationExclusive', String(value));
  }

  // "Exclude comments" for the messages filter and "Exclude messages" for the comments one
  protected get conversationExclusiveLabel(): string {
    return this.chatService.selectedConversationType === CHAT_CONVERSATION_TYPE.COMMENTS
      ? 'conversation_type.exclude_chats'
      : 'conversation_type.exclude_comments';
  }

  private syncConversationExclusiveMenuItem() {
    const item = this.chatListMenuItems.find((v) => v.key === 'conversation_exclusive');
    if (!item) return;
    item.title = this.conversationExclusiveLabel;
    item.model = this.chatService.isConversationTypeExclusive;
  }

  protected updateChats() {
    this.chatService.selectedChannels = this.formChannels.getRawValue();
    this.chatService.selectedTags = this.formTags.getRawValue();
    this.chatService.selectedManagers = this.formManagers.getRawValue();
    this.chatService.resetAndInitChatList();
    this.initFiltersBadge();
    const channelsParsedToJSON = JSON.stringify(this.chatService.selectedChannels);
    localStorage.setItem('selectedChannels', channelsParsedToJSON);
    localStorage.setItem('selectedChatTags', JSON.stringify(this.chatService.selectedTags));
    localStorage.setItem('selectedChatManagers', JSON.stringify(this.chatService.selectedManagers));
  }

  private getFilterControl(group: CHAT_FILTER_GROUP): FormControl {
    if (group === 'channel') return this.formChannels;
    if (group === 'tag') return this.formTags;
    return this.formManagers;
  }

  protected getSelectedIds(group: CHAT_FILTER_GROUP): string[] {
    return this.getFilterControl(group).value ?? [];
  }

  protected getFilterOptions(group: CHAT_FILTER_GROUP): IChatFilterOption[] {
    if (group === 'channel') return this.channels as IChatFilterOption[];
    if (group === 'tag') return this.tags;
    return this.managers;
  }

  /** "Without tags" / "without a manager" on top, hidden while searching */
  protected getNoneOptionKey(group: CHAT_FILTER_GROUP): string | null {
    if (this.filterQuery[group].trim()) return null;
    if (group === 'tag') return 'chat_filter_without_tags';
    if (group === 'manager') return 'chat_filter_without_manager';
    return null;
  }

  protected getFilterQuery(group: CHAT_FILTER_GROUP): string {
    return this.filterQuery[group];
  }

  protected setFilterQuery(group: CHAT_FILTER_GROUP, query: string): void {
    this.filterQuery[group] = query;
  }

  protected getVisibleFilterOptions(group: CHAT_FILTER_GROUP): IChatFilterOption[] {
    const query = this.filterQuery[group].trim().toLowerCase();
    const options = this.getFilterOptions(group);
    if (!query) return options;
    return options.filter((option) => option.name?.toLowerCase().includes(query));
  }

  protected isFilterOptionSelected(group: CHAT_FILTER_GROUP, id: string): boolean {
    return this.getSelectedIds(group).includes(id);
  }

  protected toggleFilterOption(group: CHAT_FILTER_GROUP, id: string): void {
    const selected = this.getSelectedIds(group);
    const next = selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id];
    this.getFilterControl(group).setValue(next, { emitEvent: false });
    this.updateChats();
  }

  protected clearFilters(): void {
    this.chatService.selectedChatSort = CHAT_SORT.IS_NEW;
    this.chatService.selectedConversationType = CHAT_CONVERSATION_TYPE.ALL;
    this.chatService.isConversationTypeExclusive = false;
    this.chatService.selectedChannels = [];
    this.chatService.selectedTags = [];
    this.chatService.selectedManagers = [];
    this.formChannels.setValue([], { emitEvent: false });
    this.formTags.setValue([], { emitEvent: false });
    this.formManagers.setValue([], { emitEvent: false });

    const sortMenuItem = this.chatListMenuItems.find((item) => item.key === 'sort');
    if (sortMenuItem) sortMenuItem.model = CHAT_SORT.IS_NEW;

    const conversationTypeMenuItem = this.chatListMenuItems.find((item) => item.key === 'conversation_type');
    if (conversationTypeMenuItem) conversationTypeMenuItem.model = CHAT_CONVERSATION_TYPE.ALL;
    this.syncConversationExclusiveMenuItem();

    if (this.baseService.isMobile) {
      this.chatService.selectedChatTab = CHAT_TABS.ACTIVE;
      const tabsMenuItem = this.chatListMenuItems.find((item) => item.key === 'tabs');
      if (tabsMenuItem) tabsMenuItem.model = CHAT_TABS.ACTIVE;
      localStorage.removeItem('selectedChatTab');
    }

    localStorage.removeItem('selectedChatSort');
    localStorage.removeItem('selectedChatConversationType');
    localStorage.removeItem('selectedChatConversationExclusive');
    localStorage.removeItem('selectedChannels');
    localStorage.removeItem('selectedChatTags');
    localStorage.removeItem('selectedChatManagers');

    this.chatService.resetAndInitChatList();
    this.initFiltersBadge();
  }

  protected onChangeTab(tabKey: string) {
    this.chatService.selectedTab = this.menuItems.find((menu) => menu.key === tabKey) || this.menuItems[0];
    localStorage.setItem('selectedTabKey', this.chatService.selectedTab.key);
  }

  protected openSettings() {
    this.chatService.actionLeftMenu(LEFT_MENU.SETTINGS);
  }

  protected toggleSearch() {
    if (!this.chatService.chatsInfo.search_text) {
      this.isSearch = !this.isSearch;
    }
  }

  protected onMouseDown(event: MouseEvent) {
    event.preventDefault();
    const mouseMoveListener = this.renderer.listen('document', 'mousemove', (e) => this.onMouseMove(e));
    const mouseUpListener = this.renderer.listen('document', 'mouseup', () => {
      mouseMoveListener();
      mouseUpListener();
    });
  }

  protected onMouseMove(event: MouseEvent) {
    if (!this.leftMenu) return;
    const newWidth = event.clientX - this.leftMenu.getBoundingClientRect().left;
    if (newWidth < 0) return;
    this.leftMenu.style.setProperty('--left-menu-width', `${newWidth}px`);
    localStorage.setItem('leftMenuWidth', `${newWidth}px`);
  }

  protected checkIsShowWriteMessage() {
    if (
      this.baseService.currentCompanyInfo.settings?.is_viber &&
      this.baseService.currentCompanyInfo.settings?.is_telegram_user
    ) {
      this.isShowWriteMessage = true;
    }
  }

  protected writeMessage() {
    this.modalService.openWriteMessage();
  }

  protected readonly MENU_ITEM_TYPE = MENU_ITEM_TYPE;
  protected readonly CHAT_CONVERSATION_TYPE = CHAT_CONVERSATION_TYPE;
  protected readonly String = String;
}
